import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import midasInstance from '@rsos/midas/src/midasInstance';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import {
  setFormPageToShow,
  setIsNonCallSubmit,
  setPaneOpen,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import {
  trackSubmitAirAmbulanceRequest,
  trackSubmitAirAmbulanceRequestApiResponse,
} from '../../../../gainsightTrackings/airAmbulanceTrackings';
// import { notifySuccess } from '@rsos/utils/toastUtils';
import {
  FORM_PAGES,
  LAUNCH_TYPES,
  LANDING_ZONE_EXTERNAL_ID,
} from '../constants';
import { getSelectValue } from '../utils';
import { parseCoordinates } from '../validation';

const useFormSubmit = (
  isLoading,
  setIsLoading,
  tentativeETA,
  UUID,
  fromMapCard,
  isStandby = false, // this is true when the activate standby button is clicked
) => {
  const dispatch = useDispatch();
  const currentPSAP = useSelector(state => state.psaps.currentPSAP);
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const existingRequest = useSelector(selectExistingRequest);
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);

  const hasLocation = getMostRecentLoc(callQueue[selectedCallerID]);

  return useCallback(
    async values => {
      if (isLoading) return null;
      setIsLoading(true);

      dispatch(setIsNonCallSubmit(fromMapCard));
      dispatch(setPaneOpen(true));

      // Determine if this is an edit or new submission
      const isEditMode = !!existingRequest?.body;

      let requestId = null;

      // Use callerID as requestId if we have location and not in edit mode
      if (hasLocation && !isEditMode) {
        requestId = selectedCallerID;
      } else {
        requestId =
          existingRequest?.body?.ext_trip_id ||
          tentativeETA?.extSystemTripId ||
          existingRequest?.request_id;
      }

      trackSubmitAirAmbulanceRequest({
        requestId: requestId,
        requestType: values.launch_type,
        uuid: UUID,
        isEdit: isEditMode,
      });

      try {
        const coords = parseCoordinates(values.pickup_coordinates);

        // Determine the callflow based on whether we're in edit mode
        const callflow =
          isEditMode || isStandby
            ? '/air_ambulance/gmr_edit'
            : '/air_ambulance/gmr_air_request';

        const fromUnite = {
          logged_in_fname: currentPSAP?.contact_name || '',
          logged_in_lname: currentPSAP?.contact_name || '',
          logged_in_email: currentPSAP?.contact_email || '',
          ecc_id: currentPSAP?.account_id || '',
        };

        const payload = {
          callflow,
          variables: {
            from_unite: fromUnite,
            ...(isStandby && {
              idenStandBy: values.launch_type === LAUNCH_TYPES.STANDBY,
              idenStandByActivated: isStandby,
            }),
            trip_data: {
              location: {
                latitude: coords?.latitude?.toString() || '',
                longitude: coords?.longitude?.toString() || '',
              },
              pickup_details: values.pickup_details || '',
              trip_reason: getSelectValue(values.request_reason),
              ground_contact: values.ground_contact || '',
              pickup_location: values.pickup_location || '',
              patient_info: values.patient_info || '',
              weather_decline: values.air_service_declined === 'Yes',
              another_carrier: values.air_service_responding === 'Yes',
              multiple_units_requested:
                values.multiple_units_requested === 'Yes',
              launch_type: values.launch_type,
              // If a landing zone was selected, include its external_id
              ...(values[LANDING_ZONE_EXTERNAL_ID] && {
                external_id: values[LANDING_ZONE_EXTERNAL_ID],
              }),
            },
          },
        };
        // Add caller_phone if we have location and not in edit mode
        if (hasLocation && !isEditMode) {
          payload.variables.from_unite['caller_phone'] = `+${selectedCallerID}`;
        }

        // For edit mode, include the request ID and trip IDs
        if (isEditMode && existingRequest?.body) {
          if (existingRequest?.body?.id) {
            payload.variables.request_id = existingRequest.body.id;
          }

          // Only include ext_trip_id and olos_trip_id in edit mode
          if (existingRequest?.body?.ext_trip_id) {
            payload.variables.ext_trip_id = existingRequest.body.ext_trip_id;
          }

          if (existingRequest?.body?.olos_trip_id) {
            payload.variables.olos_trip_id = existingRequest.body.olos_trip_id;
          }
        } else if (tentativeETA && tentativeETA.extSystemTripId) {
          payload.variables.ext_trip_id = tentativeETA.extSystemTripId;
        }

        const response = await midasInstance.post(
          '/internal_auth/trigger',
          payload,
          {
            headers: { retry: true },
          },
        );

        // TODO: Uncomment this if we need it later
        // notifySuccess({
        //   message: `Air Ambulance Request ${isEditMode ? 'Updated' : 'Submitted'}`,
        // });
        trackSubmitAirAmbulanceRequestApiResponse({
          requestId: requestId,
          status: response?.status,
          error: false,
          success: response?.data?.detail,
          UUID: UUID,
          isEdit: isEditMode,
        });
      } catch (error) {
        trackSubmitAirAmbulanceRequestApiResponse({
          requestId: requestId,
          status: error?.response?.status,
          error: error?.message,
          success: false,
          UUID: UUID,
          isEdit: isEditMode,
        });
      } finally {
        dispatch(setFormPageToShow(FORM_PAGES.STATUS));
        // setIsLoading(false);
      }
    },
    [
      selectedCallerID,
      hasLocation,
      currentPSAP,
      existingRequest,
      setIsLoading,
      tentativeETA,
      UUID,
      dispatch,
      fromMapCard,
      isLoading,
      isStandby,
    ],
  );
};

export default useFormSubmit;
